import { useMemo } from "react"
import { cn } from "@/lib/utils"
import type { UnitType } from "@/types/warehouse"
import { CheckIcon } from "lucide-react"
import { Badge } from "../ui/badge"
import { useShowcasePrices } from "@/hooks/prices/useShowcasePrices"
import {
  formatDiscountPercent,
  formatShowcaseMoney,
} from "@/hooks/prices/useUnitTypesShowcasePrices"
import { RENT_SHOWCASE_PERIODS } from "@/types/price"

export type UnitTypeDetailProps = {
  unitType?: UnitType
  facilityCode?: string | null
  className?: string
  capabilities?: string[]
}

export function UnitTypeDetail({
  unitType,
  facilityCode,
  className,
  capabilities,
}: UnitTypeDetailProps) {
  const showcaseParams = useMemo(
    () => ({
      productKind: "unit" as const,
      facility: facilityCode ?? undefined,
      productCode: unitType?.code,
      showcase: [...RENT_SHOWCASE_PERIODS],
    }),
    [facilityCode, unitType?.code],
  )

  const { data, isLoading } = useShowcasePrices(showcaseParams, {
    enabled: Boolean(facilityCode && unitType?.code),
  })

  if (!unitType) {
    return (
      <div className={cn("flex h-full flex-col", className)}>
       <div className="text-[58px] leading-[56px] font-semibold text-foreground">
        Выберите размер бокса
       </div>
      </div>
    )
  }

  const monthly = data?.items.find((item) => item.period === "1m")
  const weekly = data?.items.find((item) => item.period === "1w")
  const sixMonths = data?.items.find((item) => item.period === "6m")
  const elevenMonths = data?.items.find((item) => item.period === "11m")

  const sixMonthDiscount = sixMonths
    ? formatDiscountPercent(sixMonths.total, sixMonths.totalBefore)
    : null
  const elevenMonthDiscount = elevenMonths
    ? formatDiscountPercent(elevenMonths.total, elevenMonths.totalBefore)
    : null

  const monthlyLabel = monthly
    ? formatShowcaseMoney(monthly.total)
    : isLoading
      ? "…"
      : "—"
  const weeklyLabel = weekly
    ? formatShowcaseMoney(weekly.total)
    : isLoading
      ? "…"
      : "—"

  return (
    <div className={cn("grid h-full min-h-0 grid-rows-[1fr_auto] lg:pb-24 gap-6", className)}>
      <div>
        <h2 className="mb-3 text-[32px] leading-[36px] font-semibold text-foreground">
          Бокс {unitType.surface} м²
        </h2>

        {unitType.description ? (
          <p className="mb-8 text-[18px] leading-[24px] text-light-muted">
            {unitType.description}
          </p>
        ) : null}

        {Array.isArray(capabilities) && capabilities.length > 0 ? (
          <div className="flex lg:flex-col gap-4 mt-8 max-lg:flex-wrap">
            {capabilities.map((capability) => (
              <span key={capability} className="text-[18px] leading-[24px] font-medium text-foreground flex items-center gap-[10px]">
                <div className="size-6 rounded-[2px] bg-background flex items-center justify-center">
                  <CheckIcon className="size-4" />
                </div>
                {capability}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-[22px]">
          <div className="flex flex-col gap-2">
            <span className="text-[12px] leading-[14px] font-medium text-light-muted">
              на месяц
            </span>
            <span className="text-[32px] leading-[36px] font-bold text-foreground">
              {monthlyLabel}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-[12px] leading-[14px] font-medium text-light-muted">
              на неделю
            </span>
            <span className="text-[32px] leading-[36px] font-bold text-foreground">
              {weeklyLabel}
            </span>
          </div>
        </div>
        {(sixMonthDiscount || elevenMonthDiscount) ? (
          <div className="flex lg:items-center gap-1 lg:flex-row flex-col">
            {sixMonthDiscount ? (
              <Badge className="text-[12px] leading-[16px] font-medium text-pink bg-background rounded-[2px] px-4 py-1 h-auto">
                -{sixMonthDiscount}% при оплате за 6 месяцев
              </Badge>
            ) : null}
            {elevenMonthDiscount ? (
              <Badge className="text-[12px] leading-[16px] font-medium text-pink bg-background rounded-[2px] px-4 py-1 h-auto">
                -{elevenMonthDiscount}% при оплате за 11 месяцев
              </Badge>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
